import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { HttpErrorClaimHandler } from '@mp/shared/data-access';

import { MaintenanceModeSignalService } from './maintenance-mode-signal.service';

/**
 * Der Antwort-Header, mit dem die Wartungssperre des Web-Hosts ihre 503er markiert.
 *
 * Er unterscheidet die Sperre von den körperlosen 503ern der Migrations-Middleware und eines Load-Balancers.
 */
export const MAINTENANCE_MODE_HEADER = 'x-mediio-maintenance';

/**
 * Der Header-Wert, mit dem eine aktive Wartungssperre gemeldet wird.
 */
export const MAINTENANCE_MODE_HEADER_ACTIVE_VALUE = 'active';

/**
 * Ob der angegebene Fehler ein von der Wartungssperre markierter 503 ist.
 *
 * Andere Feature-Schichten (z. B. Feedback- und Hilfe-Center-Widgets) nutzen dies, um ihre eigene,
 * generische Fehlermeldung zu unterdrücken: ein solcher 503 ist keine Störung, sondern die geplante Sperre,
 * die bereits über die Sperrmeldung der Wartungs-Feature-Schicht angezeigt wird.
 * @param error The error to check, typically caught from an HTTP call.
 * @returns Whether the error is a maintenance lock.
 */
export function isMaintenanceLockError(error: unknown): error is HttpErrorResponse {
  return (
    error instanceof HttpErrorResponse &&
    error.status === 503 &&
    error.headers.get(MAINTENANCE_MODE_HEADER) === MAINTENANCE_MODE_HEADER_ACTIVE_VALUE
  );
}

/**
 * Übernimmt die markierten 503er der Wartungssperre aus der globalen Fehlerbehandlung.
 *
 * Ein solcher 503 ist keine Störung, sondern die geplante Sperre: gemeldet wird sie über die Sperrmeldung
 * der Wartungs-Feature-Schicht, nicht über eine generische Fehlermeldung.
 */
@Injectable({ providedIn: 'root' })
export class MaintenanceLockClaimHandler implements HttpErrorClaimHandler {
  private readonly maintenanceModeSignalService = inject(MaintenanceModeSignalService);

  /**
   * Claims a 503 that the maintenance lock has marked as its own.
   * @param error The error of the failed request.
   * @returns Whether the error was claimed.
   */
  claim(error: HttpErrorResponse): boolean {
    if (!isMaintenanceLockError(error)) {
      return false;
    }

    this.maintenanceModeSignalService.markLocked(readEndUtc(error.error));

    return true;
  }
}

function readEndUtc(body: unknown): string | null {
  const endUtc = typeof body === 'object' && body !== null ? (body as Record<string, unknown>)['endUtc'] : null;

  return typeof endUtc === 'string' ? endUtc : null;
}
